import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';

import { Paperwork } from '@shared/interfaces/paperwork.interface';
import { Medicine, Recipe } from '@shared/interfaces/recipe.interface';
import { MagistralRecipe } from '@shared/interfaces/recipe-magistral.interface';

import { API_URIS } from '@shared/constants/api-uris.const';
import { PCV2Request } from '@shared/interfaces/request.interface';

import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { StatusService } from '@shared/services/status.service';
import { MedicineNew } from '@shared/interfaces/recipe-v2.interface';

const BASE_API_PCV2: string = environment.baseApiPCv2;
const BASE_API_RME: string = environment.baseApiRME;
const URI_CLINICAL_RECIPES: string = API_URIS.clinical.orders;

/**
 * Servicio para la gestión de recetas médicas.
 *
 * @description
 * Proporciona métodos para interactuar con el backend en operaciones relacionadas
 * con recetas médicas, incluyendo búsqueda de medicamentos en el vademécum y
 * almacenamiento de prescripciones médicas.
 */
@Injectable({
    providedIn: 'root',
})
export class RecipeService {

    constructor(
        private httpClient: HttpClient,
        private statusService: StatusService) { }

    /**
     * Busca medicamentos en el vademécum según criterios especificados.
     *
     * @description
     * Realiza una búsqueda de medicamentos en la base de datos del vademécum utilizando
     * diferentes criterios (principio activo o nombre comercial) y alcance definido.
     * Retorna una lista de medicamentos que coinciden con el texto de búsqueda.
     *
     * @param {string} query - Tipo de búsqueda: 'principio-activo' o 'nombre-comercial'
     * @param {string} text - Texto de búsqueda a consultar en el vademécum
     * @param {string} scope - Alcance de la búsqueda (ej: 'RMG', 'RMD', 'RMM')
     * @returns {Observable<{ status: string; request: any }>} Observable con el estado de la petición y los medicamentos encontrados
     *
     * @remarks
     * - Si el texto está vacío, retorna un estado 'void' con array vacío
     * - Si no hay resultados, retorna un estado 'void'
     * - Si hay error de tipo ValueError, retorna un estado 'void'
     * - En caso exitoso, adapta la respuesta usando adaptMedicineResponse
     */
    getPrescriptionsV2(query: string, text: string, scope: string): Observable<{ status: string; request: any }> {
        if (text === '') return of({ status: 'void', request: [] });
        return this.httpClient.get<PCV2Request>(`${BASE_API_PCV2}/servicios/v1/medicinas/vademecum?tipo=${query}&consulta=${text}&alcance=${scope}`).pipe(
            map(({ data }) => {
                if (data.length === 0) return this.statusService.status('void', []);
                if (data.errorType === 'ValueError') return this.statusService.status('void', []);
                return this.statusService.status('ok', this.adaptMedicineResponse(data));
            }),
            catchError(this.statusService.catchError),
        );
    };

    /**
     * Adapta la respuesta del backend al formato esperado de medicamentos.
     *
     * @description
     * Normaliza la estructura de datos de medicamentos recibidos del backend,
     * asegurando que todos los campos requeridos estén presentes con valores
     * por defecto en caso de estar ausentes.
     *
     * @param {MedicineNew[]} data - Array de medicamentos recibidos del backend
     * @returns {MedicineNew[]} Array de medicamentos con estructura normalizada
     *
     * @remarks
     * Asegura valores por defecto:
     * - Arrays vacíos para ids y dosages
     * - Strings vacíos para campos de texto opcionales
     */
    adaptMedicineResponse(data: MedicineNew[]): MedicineNew[] {
        return data.map((item) => ({
            ids: item.ids || [],
            type: item.type || '',
            code: item.code || '',
            description: item.description || '',
            compounds: item.compounds || '',
            image: item.image || '',
            dosages: item.dosages || [],
        }));
        }

    /**
     * Guarda una receta médica en el sistema.
     *
     * @description
     * Envía una receta médica (general o magistral) al backend para su almacenamiento
     * y procesamiento. Genera el documento oficial y lo registra en el sistema de
     * historias clínicas electrónicas.
     *
     * @param {Paperwork<Recipe | MagistralRecipe>} body - Objeto de trámite conteniendo la receta a guardar
     * @returns {Observable<Paperwork<Recipe | MagistralRecipe>>} Observable con los datos del trámite guardado
     *
     * @remarks
     * - Soporta tanto recetas médicas generales como recetas magistrales
     * - El backend se encarga de generar el PDF, firmar digitalmente y notificar al paciente
     * - Retorna el objeto completo del trámite con su ID asignado
     */
    putRecipe(body: Paperwork<Recipe | MagistralRecipe>): Observable<Paperwork<Recipe | MagistralRecipe>> {
        return this.httpClient.post<PCV2Request>(`${BASE_API_PCV2}/${URI_CLINICAL_RECIPES}`, body).pipe(
          map(({ data }: { data: Paperwork<Recipe | MagistralRecipe> }) => data),
        );
    }

}
